import { isDevEnv } from '@/utils/index';

/**
 * Determine whether to enable log output
 * Priority logic:
 * 1. If in mock mode (VUE_APP_IS_MOCK) OR development mode (NODE_ENV === 'development'), enable log directly
 * 2. If in dev environment (isDevEnv = true), enable log directly
 * 3. If in Node.js environment (window undefined), disable log
 * 4. In production browser environment, enable log only when ALL conditions are met:
 *    - Protocol must be HTTPS
 *    - Domain must match *.zoomdev.us (e.g., zoomdev.us, deva.zoomdev.us, dev-integration.zoomdev.us)
 *    - URL must contain 'billing_log' parameter
 *
 * @returns {boolean} Whether to enable log output
 */
export function shouldEnableLog() {
  // Priority 1: If in mock mode or development mode, enable log directly
  if (process.env.VUE_APP_IS_MOCK || process.env.NODE_ENV === 'development') return true;

  // Priority 2: If in dev environment, enable log directly
  if (isDevEnv) return true;

  // Priority 3: Check if in browser environment
  if (typeof window === 'undefined') {
    return false;
  }

  // Priority 4: In non-dev browser environment, check all three conditions progressively
  // Condition 1: Must be HTTPS protocol
  const isHttps = window.location.protocol === 'https:';
  if (!isHttps) return false;

  // Condition 2: Domain must match zoomdev.us or its subdomains
  // Regex matches: zoomdev.us, deva.zoomdev.us, dev-integration.zoomdev.us, etc.
  const isDomainMatch = /^([a-z0-9]([a-z0-9-]*[a-z0-9])?\.)?zoomdev\.us$/i.test(window.location.hostname);  if (!isDomainMatch) return false;

  // Condition 3: URL must contain 'billing_log' parameter
  // Only execute URLSearchParams parsing when previous conditions are met (performance optimization)
  const hasBillingLogParam = new URLSearchParams(window.location.search).has('billing_log');
  return hasBillingLogParam;
}

// Calculate once at module load time for better performance
// Avoid repeated function calls on every log invocation
const ENABLE_LOG = shouldEnableLog();

/**
 * Logger class that wraps console methods with conditional output
 * All methods will only output when ENABLE_LOG is true
 */
export class Logger {
  info() {
    ENABLE_LOG && console.info(...arguments);
  }
  log() {
    ENABLE_LOG && console.log(...arguments);
  }
  error() {
    ENABLE_LOG && console.error(...arguments);
  }
  warn() {
    ENABLE_LOG && console.warn(...arguments);
  }
  table() {
    ENABLE_LOG && console.table(...arguments);
  }
  debug() {
    ENABLE_LOG && console.debug(...arguments);
  }
  groupCollapsed() {
    ENABLE_LOG && console.groupCollapsed(...arguments);
  }
  groupEnd() {
    ENABLE_LOG && console.groupEnd();
  }
}

// Export singleton instance for global use
export const logger = new Logger();
